import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Feather, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = isLogin ? "/auth/login" : "/auth/register";
      const body = isLogin ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, body);
      login(data.token, data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-900 grain flex flex-col relative overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-gold/8 blur-[150px] pointer-events-none" />
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <button className="flex items-center gap-2.5" onClick={() => navigate("/")}>
          <Feather className="w-6 h-6 text-gold" />
          <span className="font-serif text-2xl font-bold text-slate-100">{t("app.name")}</span>
        </button>
        <LanguageToggle />
      </header>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md card-surface rounded-2xl p-8 md:p-10 gold-glow"
        >
          <h1 className="font-serif text-3xl font-bold text-slate-100">{t(isLogin ? "auth.login.title" : "auth.register.title")}</h1>
          <p className="mt-2 font-sans text-sm text-slate-400">{t(isLogin ? "auth.login.subtitle" : "auth.register.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {!isLogin && (
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-wide">{t("auth.name")}</Label>
                <Input
                  data-testid="auth-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="bg-ink-800 border-white/10 text-slate-100 h-11 focus-visible:ring-gold"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wide">{t("auth.email")}</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="bg-ink-800 border-white/10 text-slate-100 h-11 focus-visible:ring-gold"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wide">{t("auth.password")}</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="bg-ink-800 border-white/10 text-slate-100 h-11 focus-visible:ring-gold"
              />
            </div>

            {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full bg-gold hover:bg-gold-light text-ink-900 font-semibold h-11 rounded-lg mt-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t(isLogin ? "auth.login.button" : "auth.register.button")}
            </Button>
          </form>

          <button
            onClick={() => navigate(isLogin ? "/register" : "/login")}
            className="mt-6 w-full text-center text-sm text-slate-400 hover:text-gold transition-colors"
            data-testid="auth-switch-btn"
          >
            {t(isLogin ? "auth.toRegister" : "auth.toLogin")}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
